import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Motion resolves through the interaction layer's tokens rather than
  // hardcoded values. Two reasons this changed:
  //   1. `duration-200 ease-out` sat outside the layer, so the shared
  //      prefers-reduced-motion guard (which zeroes --fx-dur*/--fx-lift/
  //      --fx-press) did not reach the most-used interactive element in the
  //      portal.
  //   2. This carried `active:translate-y-[1px]` with NO hover counterpart —
  //      the exact bug already fixed for .fx-btn (portal-brand.css:620): the
  //      button sank below its resting position on click and never rose. It
  //      now lifts to meet the pointer and returns on press.
  // Icon motion is not declared here; icon-motion.css assigns it by icon
  // meaning for every interactive element, not just Buttons.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--fx-r-pill)] text-sm font-semibold ring-offset-background transition-all [transition-duration:var(--fx-dur,180ms)] [transition-timing-function:var(--fx-ease,ease-out)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-500)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:translate-y-[var(--fx-lift,-1px)] active:translate-y-0 active:scale-[var(--fx-press,0.98)]",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--fx-ink-900)] text-[var(--fx-paper)] hover:bg-[var(--fx-accent-700)] dark:bg-[var(--fx-paper)] dark:text-[var(--fx-ink-900)] dark:hover:bg-[var(--fx-accent-300)]",
        // Tinted. For a destructive action sitting in a toolbar or a row,
        // where a solid red would outshout every neighbouring control.
        destructive:
          "bg-[rgba(201,94,62,0.12)] text-[var(--fx-warn-text)] hover:bg-[rgba(201,94,62,0.18)] dark:bg-[rgba(232,153,120,0.14)] dark:text-[#E89978] dark:hover:bg-[rgba(232,153,120,0.22)]",
        // Solid. For the confirming action of a destructive dialog — the
        // point of no return, where the button must not be mistakable for
        // the safe choice. See --fx-s-danger for the contrast note.
        destructiveSolid:
          "bg-[var(--fx-s-danger)] text-white hover:bg-[var(--fx-s-danger-hover)]",
        outline:
          "border border-[var(--fx-ink-200)] bg-[var(--fx-white)] text-[var(--fx-ink-900)] hover:bg-[var(--fx-s-raised-hover)] hover:border-[var(--fx-ink-300)] dark:border-[var(--dk-border-2)] dark:bg-transparent dark:text-[var(--dk-text)] dark:hover:bg-[var(--fx-s-raised-hover)]",
        secondary:
          "bg-[var(--fx-s-raised-hover)] text-[var(--fx-ink-900)] hover:bg-[var(--fx-s-raised-active)] dark:bg-[var(--fx-s-raised-hover)] dark:text-[var(--dk-text)] dark:hover:bg-[var(--fx-s-raised-active)]",
        ghost:
          "text-[var(--fx-ink-700)] hover:bg-[var(--fx-s-raised-hover)] hover:text-[var(--fx-ink-900)] dark:text-[var(--dk-text-soft)] dark:hover:bg-[var(--fx-s-raised-hover)] dark:hover:text-[var(--dk-text)]",
        link: "text-[var(--fx-accent-700)] underline-offset-4 hover:underline dark:text-[var(--fx-accent-300)]",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 px-4",
        lg: "h-11 px-7",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
